"use client";
import { Close } from "@mui/icons-material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { motion } from "framer-motion";
import { useState } from "react";
import careerImage from "../../assets/career/career-image-2.jpg";
import ApplyModal from "./ApplyModal";

const CareerPage = () => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return; // ignore click outside
    setSnackbarOpen(false);
  };
  const [selectedJob, setSelectedJob] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isApplyOpen, setIsApplyOpen] = useState(false);

  const jobs = [
    {
      title: "Frontend Developer",
      location: "Vashi",
      exp: "1–3 years",
      jd: "Build and optimize scalable, high-performance frontends using modern frameworks. Collaborate with designers and backend engineers to deliver seamless user experiences.",
      skills: [
        "React.js / Next.js",
        "JavaScript (ES6+), TypeScript",
        "HTML5, CSS3, TailwindCSS / Material UI",
        "Responsive & Cross-Browser Development",
        "REST APIs / GraphQL",
        "Version Control (Git/GitHub)",
        "Basic Testing (Jest/React Testing Library)",
        "Understanding of SEO & Performance Optimization",
      ],
    },
    {
      title: "UI/UX Designer",
      location: "Vashi",
      exp: "1–3 years",
      jd: "Design intuitive, user-friendly interfaces and create engaging user experiences. Collaborate with developers to ensure seamless implementation of designs.",
      skills: [
        "Figma / Adobe XD / Sketch",
        "Wireframing & Prototyping",
        "User Research & Usability Testing",
        "Design Systems & Style Guides",
        "Responsive & Mobile-First Design",
        "Collaboration with Developers",
        "Basic Knowledge of HTML/CSS (preferred)",
      ],
    },
    {
      title: "Backend Engineer (Java Developer)",
      location: "Vashi",
      exp: "1–3 years",
      jd: "Design, develop, and maintain backend services and APIs using Java. Work with Spring Boot to build scalable microservices, manage databases, and ensure system performance and security.",
      skills: [
        "Java (Core & Advanced)",
        "Spring Boot Framework",
        "SQL Server",
        "Hibernate / JPA",
        "Git / GitHub",
      ],
    },
    {
      title: "Mobile Application Developer",
      location: "Vashi",
      exp: "1–3 years",
      jd: "Develop and maintain cross-platform mobile applications with a focus on performance and user experience. Work closely with designers and backend teams to deliver high-quality apps.",
      skills: [
        "React Native / Flutter",
        "JavaScript / TypeScript",
        "Android & iOS Development Basics",
        "REST APIs & JSON",
        "Firebase / Push Notifications",
        "Mobile UI/UX Best Practices",
        "Debugging & Performance Optimization",
        "Git / GitHub",
      ],
    },
  ];

  return (
    <div className="bg-white text-gray-900">
      {/* Hero Section */}
      <section className="bg-[#112D5F] text-white text-center py-20 px-4">
        <h1 className="text-4xl md:text-5xl font-bold">
          Join Our Team at Flint Infotech
        </h1>
        <p className="mt-4 text-lg max-w-2xl mx-auto">
          We are building the future of digital innovation. Explore
          opportunities with us.
        </p>
        <div className="mt-6 flex gap-4 justify-center flex-wrap">
          <Button
            variant="contained"
            size="large"
            className="bg-[#41498D]"
            onClick={() => scrollToSection("job_openings")}
          >
            View Openings
          </Button>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-16 max-w-6xl mx-auto text-center px-4">
        <h2 className="text-3xl font-semibold">Why Work With Us</h2>
        <div className="grid md:grid-cols-4 sm:grid-cols-2 gap-8 mt-10">
          {[
            {
              title: "Innovative Culture",
              desc: "Work on cutting-edge technologies.",
            },
            {
              title: "Global Clients",
              desc: "Collaborate with international brands.",
            },
            {
              title: "Growth Opportunities",
              desc: "Upskill and grow your career.",
            },
            {
              title: "Great Team",
              desc: "Work with passionate professionals.",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.05 }}
              className="bg-gray-100 rounded-2xl p-6 shadow-md"
            >
              <h3 className="text-xl font-medium">{item.title}</h3>
              <p className="mt-2 text-gray-600">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Life at Flint */}
      <section className="py-16 bg-gray-50 px-4">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <img
            src={careerImage}
            alt="Life at Flint"
            className="rounded-2xl shadow-lg"
          />
          <div>
            <h2 className="text-3xl font-semibold">Life at Flint</h2>
            <p className="mt-4 text-gray-600">
              Our culture celebrates innovation, teamwork, and growth. From
              office events to collaborative projects, Flint Infotech is more
              than just a workplace — it’s a community.
            </p>
          </div>
        </div>
        <div id="job_openings" className=""></div>
      </section>

      {/* Open Positions */}
      <section className="py-16 px-4">
        <h2 className="text-3xl font-semibold text-center">Open Positions</h2>
        <div className="max-w-4xl mx-auto mt-8 space-y-4">
          {jobs.map((job, i) => (
            <div
              key={i}
              className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-xl shadow-md"
            >
              <div>
                <h3 className="text-lg font-bold">{job.title}</h3>
                <p className="text-gray-500">
                  {job.location} • {job.exp}
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex gap-3">
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#091e43",
                    "&:hover": {
                      backgroundColor: "#0D3559", // slightly different on hover
                    },
                    color: "#fff", // text color
                    fontWeight: 500,
                  }}
                  onClick={() => {
                    setSelectedJob(job);
                    setIsOpen(true);
                  }}
                >
                  Read More
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedJob(job);
                    setIsOpen(false); // close JD modal
                    setIsApplyOpen(true); // open Apply modal
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div id="apply_now"></div>
      </section>

      {/* Job Description Modal */}
      {isOpen && selectedJob && (
        <Dialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          fullWidth
          maxWidth="sm"
          className="mt-10"
        >
          <DialogTitle>{selectedJob.title}</DialogTitle>
          <DialogContent>
            <p className="text-gray-500">
              {selectedJob.location} • {selectedJob.exp}
            </p>
            <p className="mt-4 text-gray-700">{selectedJob.jd}</p>
          </DialogContent>

          {/* Skills Section */}
          {selectedJob.skills && (
            <DialogContent>
              <h4 className="font-semibold mb-2">Required Skills:</h4>
              <div className="flex flex-wrap gap-2">
                {selectedJob.skills.map((skill, idx) => (
                  <Tooltip key={idx} arrow>
                    <p className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full hover:bg-blue-600 hover:text-white transition cursor-pointer">
                      {skill}
                    </p>
                  </Tooltip>
                ))}
              </div>
            </DialogContent>
          )}

          <DialogActions>
            <Button onClick={() => setIsOpen(false)} variant="outlined">
              Close
            </Button>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#091e43",
                "&:hover": {
                  backgroundColor: "#0D3559", // slightly different on hover
                },
                color: "#fff", // text color
                fontWeight: 500,
              }}
              onClick={() => {
                setIsOpen(false); // close JD modal
                setIsApplyOpen(true); // open Apply modal
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Full Apply Modal */}
      {isApplyOpen && (
        <ApplyModal
          open={isApplyOpen}
          onClose={() => setIsApplyOpen(false)}
          job={selectedJob}
          showSnackbar={(msg, type = "success") => {
            setSnackbarMsg(msg);
            setSnackbarType(type);
            setSnackbarOpen(true);
          }}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        onClose={handleCloseSnackbar}
        autoHideDuration={4000} // auto-close after 4 seconds
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarType} // success, error, warning, info
          variant="filled"
          sx={{ width: "100%", textAlign: "center" }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseSnackbar}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CareerPage;
